import {FC, useEffect, useId, useRef} from 'react'
import {createPortal} from 'react-dom'

import {TriangleWarningIcon} from '../SvgIcon'
import {useTranslation} from 'react-i18next'
import {LoadingSpinner, RenderIf} from 'components'
import {colorCodes, KEY_CODES} from 'definitions/constant'
import {useMobileViewport} from 'hooks/useMobileViewport'

interface RouterProps {
  onOK: () => void
  onCancel: () => void
  title?: string
  okText?: string
  cancelText?: string
  ariaLabel?: string
  isConfirming?: boolean
}

export const RouterPrompt: FC<RouterProps> = props => {
  const {t} = useTranslation('common')
  const {
    onOK,
    onCancel,
    title = 'Are you sure you want to leave this page? You have unsaved changes.',
    okText = 'Confirm',
    cancelText = 'Cancel',
    ariaLabel = t('CONFIRMATION_DIALOG'),
    isConfirming = false,
  } = props
  const isMobile = useMobileViewport()
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const onCancelRef = useRef(onCancel)
  onCancelRef.current = onCancel

  useEffect(() => {
    const opener = document.activeElement
    cancelRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === KEY_CODES.ESCAPE) {
        event.preventDefault()
        event.stopPropagation()
        onCancelRef.current()
      }
      if (event.key !== KEY_CODES.TAB) return
      const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>(
        'button:not([disabled])',
      )
      if (!buttons?.length) return
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (!dialogRef.current?.contains(document.activeElement)) {
        event.preventDefault()
        const nextButton = event.shiftKey ? last : first
        nextButton.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [])

  useEffect(() => {
    // Confirm is removed while the operation runs, so keep focus in the dialog.
    if (isConfirming && !dialogRef.current?.contains(document.activeElement)) {
      cancelRef.current?.focus()
    }
  }, [isConfirming])

  const dialog = (
    <div
      className="mobile-router-prompt w-full h-full z-50 bg-dope-black bg-opacity-30 fixed flex justify-center items-start mt-4 top-0 left-0 right-0 bottom-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={dialogRef}
    >
      <div className="max-w-md p-5 rounded-md shadow-2xl bg-gray-900">
        <div className="w-full flex items-start justify-between">
          <TriangleWarningIcon
            width={40}
            height={40}
            strokeColor="#e1aa0e"
            classes="-mt-1"
          />
          <h1
            id={titleId}
            data-cy="router-prompt-title-message"
            className="text-white text-base text-left karla-exl ml-4"
          >
            {title}
          </h1>
        </div>

        <div className="mt-12 flex items-center justify-end">
          <button
            className="mr-7 text-sm"
            type="button"
            ref={cancelRef}
            onClick={onCancel}
            data-cy="router-prompt-cancel-button"
            aria-label={t('CANCEL')}
          >
            {cancelText}
          </button>

          <RenderIf isTrue={!isConfirming}>
            <button
              className="text-sm"
              type="button"
              onClick={onOK}
              data-cy="router-prompt-ok-button"
              aria-label={ariaLabel}
            >
              {okText}
            </button>
          </RenderIf>

          <RenderIf isTrue={isConfirming}>
            <LoadingSpinner
              isLoading={true}
              type="TailSpin"
              color={colorCodes.white}
              width={16}
              height={16}
              ariaLabel="default bypass scanning spinner"
            />
          </RenderIf>
        </div>
      </div>
    </div>
  )

  // Phone panes that are covered or closed are transformed, hidden or inert,
  // which would trap a fixed dialog inside them; render it at the body instead.
  return isMobile ? createPortal(dialog, document.body) : dialog
}
